<template>
  <v-container v-if="state.document" class="document-viewer">
    <v-sheet class="mx-auto px-3 pb-12">
      <v-form
        validate-on="submit lazy"
        :disabled="state.document.status === 'complete'"
        class="pb-12"
      >
        <template
          v-for="(item, index) in state.document.data.items"
          :key="index"
        >
          <template v-if="item.type === 'section'">
            <form-field :item="item" @toggle-section="toggleSection">
              <template
                v-for="(subItem, subIndex) in getSectionItems(index)"
                :key="subIndex"
              >
                <span
                  v-if="subItem.type === 'text'"
                  v-html="processLinks(subItem.content)"
                  class="text-wrap"
                ></span>
                <form-field
                  v-else
                  v-model="
                    state.document.data.items[subItem.originalIndex].value
                  "
                  :item="subItem"
                  :parent-section="item"
                />
              </template>
            </form-field>
          </template>
          <template v-else-if="!isItemInSection(index)">
            <span
              v-if="item.type === 'text'"
              v-html="processLinks(item.content)"
              class="text-wrap"
            ></span>
            <form-field
              v-else
              v-model="state.document.data.items[index].value"
              :item="item"
              :data-index="index"
            />
          </template>
        </template>
      </v-form>
    </v-sheet>
  </v-container>
</template>

<script setup lang="ts">
import { reactive, computed, ref, nextTick } from "vue";
import { documentService } from "@/api/services/document";
import { useSettingsStore } from "@/api/store/settings";
import { useRouter } from "vue-router";

import FormField from "@/components/SwandoolaDocBuilder/FormField.vue";

const router = useRouter();

const settingsStore = useSettingsStore();

const state = reactive({
  document: null as any,
});

const documentId = computed(() => router.currentRoute.value.params.id);

const fetchDocument = async () => {
  documentService
    .showTemplate(documentId.value as string)
    .then((response) => {
      state.document = response;
    })
    .catch((error) => {
      console.log(error);
    });
};

fetchDocument();

const checkRequiredFields = async (data: any): Promise<boolean> => {
  for (let index = 0; index < data.items.length; index++) {
    const item = data.items[index];

    if (
      item.isRequired &&
      (!item.value || item.value.toString().trim() === "")
    ) {
      await nextTick();

      const el = document.querySelector(
        `[data-index="${index}"]`
      ) as HTMLElement;

      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.focus?.();
      }

      alert(`Please fill in the required field: "${item.label}"`);
      return false;
    }
  }

  return true;
};

const processLinks = (content: any) => {
  if (!content || content.length === 0) {
    return content;
  }

  // Regular expression to find URLs in text
  const urlRegex = /\bhttps?:\/\/[^\s<>]+/g;

  return content.replace(
    urlRegex,
    (url) => `<a href="${url}" target="_blank">${url}</a>`
  );
};

const submit = async (completed: boolean) => {
  if (completed && !(await checkRequiredFields(state.document.data))) {
    return;
  }

  const response = await documentService.update(state.document.id, {
    data: state.document.data,
    completed: completed,
  });

  fetchDocument();
};

const getSectionItems = (sectionIndex: number) => {
  const items = state.document.data.items;
  const nextSectionIndex = items.findIndex(
    (item, index) => index > sectionIndex && item.type === "section"
  );

  const endIndex = nextSectionIndex === -1 ? items.length : nextSectionIndex;

  return items
    .slice(sectionIndex + 1, endIndex)
    .map((item: any, index: number) => ({
      ...item,
      originalIndex: sectionIndex + 1 + index,
    }));
};

const isItemInSection = (index: number) => {
  const items = state.document.data.items;
  const previousItems = items.slice(0, index);
  const lastSection = previousItems.findLast((item) => item.type === "section");
  return !!lastSection;
};

const toggleSection = (sectionItem: any) => {
  sectionItem.isExpanded = !sectionItem.isExpanded;
};
</script>

<style scoped>
.text-wrap {
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  display: block;
}

:deep(ol) {
  padding-left: 14px;
}

:deep(p) {
  font-family: Helvetica, Arial, sans-serif;
  line-height: 1.625;
  font-weight: 300;
  margin-bottom: 1rem;
  color: black;
}

:deep(b, strong) {
  font-weight: bolder;
}

:deep(hr) {
  margin: 1rem 0;
  opacity: 0.5;
}

.fixed-buttons {
  position: fixed;
  bottom: 0;
  padding-bottom: 20px;
  width: 425px;
  background: white;
}
</style>
